/**
 * The phone's home- and lock-screen widgets (plan.md step 79; the user,
 * 2026-10-28): Live mode, Full schedule, My schedule and Live map.
 *
 * A widget has no data of its own and never fetches: the rave page hands the
 * phone app a `WidgetPlan` built from what the APP holds offline — the stored
 * bundle, the queued saves, the stored forecast (the app's own offline rules)
 * — and only for a rave this signed-in account ATTENDS. The shell turns it
 * into the snapshot the native widgets draw (`mobile/src/widgetShell.ts`).
 *
 * The rules a widget follows are here, ONCE, run by the shell: which rave is
 * "Active rave", the Live-mode card (the always-on card's own rule,
 * `liveCardFor`), the rows of the two lists, the Live map's crop and marks.
 * Swift and Kotlin only filter the given rows by the time of an entry
 * (`widgetRows`, checked against `widgetFixture`) and draw. Pure: it also
 * runs in React Native.
 */
import { fitTps, geoToImage, haversineMetres, isOnMap, type ControlPoint } from './geo.js';
import { isOnNow } from './festivalMode.js';
import { sunTimes } from './sun.js';
import { toOffsetISO, wallClock } from './time.js';
import type { LiveCardAct, LiveCardMode, LiveCardPlan, LiveCardStage, LiveCardWords } from './liveCard.js';
import type { WeatherCondition, WeatherSource, WeatherUnits } from './weather.js';

/** The snapshot's schema — a widget refuses (fails closed on) any other. */
export const WIDGET_SCHEMA = 1;
/** The Live map's GPS mark is hidden once the fix is older than this (the user's requirement, 2026-10-28). */
export const WIDGET_MARK_FRESH_MS = 15 * 60_000;
/** How much of a rave a plan carries: every set left, capped for the bridge. */
export const MAX_WIDGET_ACTS = 3_000;
export const MAX_WIDGET_POIS = 300;
export const MAX_WIDGET_DAYS = 31;
export const MAX_WIDGET_HOURS = 48;
/** The always-on card's window, for Live mode (`CARD_WINDOW_MS` in the page). */
export const WIDGET_CARD_WINDOW_MS = 24 * 60 * 60_000;

/**
 * The widgets' own words in the reader's language (the page translates them,
 * like the card's `LIVE_CARD_WORDS`); Swift and Kotlin fall back to English
 * for any missing.
 */
export const WIDGET_WORDS = [
  /** "Sign in to use MyRave widgets" — every widget, signed out. */
  'signIn',
  /** "Attend a rave to see it here" — signed in, no attended rave. */
  'attend',
  /** "Thank you — see you next time" — after the rave. */
  'thankYou',
  'allStages',
  /** The weather row's kinds: "Forecast", "Typical". */
  'forecast', 'typical',
  /** "No more sets", "No saved sets yet". */
  'noMoreSets', 'noSaved',
  /** "Location from {clock}" — the GPS mark's age on the Live map. */
  'markFrom',
  /** "No location" — the Live map without a fresh fix. */
  'noLocation',
  /** "You're outside the map" — a fresh fix that is off the rave's map (the user, 2026-10-29). */
  'outsideMap',
  /** "Gates open in" — the countdown's label on the two lists. */
  'gatesOpenIn',
  /** The switch-stage button's accessibility label: "Show the next stage". */
  'switchStage',
  /** "Live now" — a stage playing, on the map. */
  'liveNow',
  'dir',
  /** The buttons of the safe states, and the Live map with no map: "Sign in", "Find a rave", "No map for this rave". */
  'signInButton', 'findRave', 'noMap',
  /** "{days}d {hours}h" — a countdown a day or more out; "Forecast from {date}, {time}" — the forecast's age. */
  'countdownDays', 'forecastFrom',
  /** The reader's language tag ("he-IL") — the shell writes the weekdays in it. */
  'locale',
] as const;
export type WidgetWord = (typeof WIDGET_WORDS)[number];
export type WidgetWords = Partial<Record<WidgetWord, string>>;

export interface WidgetStage extends LiveCardStage {
  /** Its place on the map drawing (0..1 fractions), when the map has its pin. */
  x?: number | null;
  y?: number | null;
  /** Its category (the rave's track — Music, Body & Mind…): the lists group by it. */
  trackId?: string | null;
}

/** A category of the rave (a track), in the reader's words — the lists' groups, Music first. */
export interface WidgetTrack { id: string; label: string; icon?: string | null }

/**
 * The categories the lists group by (the 0.14.82 walk, Android: "it should show by category and music comes
 * first"): Music first, then the rave's own order — only those with a stage.
 */
export function widgetTracks(
  tracks: readonly { id: string; slug: string; label: string; icon?: string | null; sortOrder: number }[],
  stages: readonly { trackId?: string | null }[],
): WidgetTrack[] {
  const used = new Set(stages.map((s) => s.trackId).filter(Boolean));
  return tracks
    .filter((t) => used.has(t.id))
    .sort((a, b) => Number(b.slug === 'music') - Number(a.slug === 'music') || a.sortOrder - b.sortOrder || a.id.localeCompare(b.id))
    .map((t) => ({ id: t.id, label: t.label, ...(t.icon ? { icon: t.icon } : {}) }));
}

export interface WidgetDay {
  /** The rave's day, `YYYY-MM-DD` in its zone. */
  date: string;
  source: WeatherSource;
  highC: number | null;
  lowC: number | null;
  condition: WeatherCondition;
}

export interface WidgetMapInput {
  imageUrl: string;
  width: number;
  height: number;
  controlPoints: ControlPoint[];
  /** The points of interest, as the map page shows them. */
  points: { id: string; name: string; category: string | null; x: number; y: number }[];
}

/** Page → shell (`widget.plan`): one attended rave, from the app's offline data. */
export interface WidgetPlan {
  editionId: string;
  rave: string;
  /** The festival's name — "The gates to Imagine". */
  festivalName?: string | null;
  timezone: string;
  gatesAt: string | null;
  endsAt: string;
  /** The rave's own address — the base of every tap. */
  url?: string | null;
  logoUrl?: string | null;
  coverUrl?: string | null;
  lat?: number | null;
  lng?: number | null;
  /** Live mode's stage: as the always-on card has it. */
  mode: LiveCardMode;
  stageId: string | null;
  radiusM: number;
  stages: WidgetStage[];
  /** The categories, Music first (`widgetTracks`) — absent from an older page: the lists do not group. */
  tracks?: WidgetTrack[];
  /** EVERY set left in the rave, on every stage — not only the card's next day. */
  acts: LiveCardAct[];
  /** The saved sets, as My lineup shows them (offline saves not yet synced included). */
  saved: string[];
  weather?: {
    units: WeatherUnits;
    /** When the forecast was fetched — its age is shown, never hidden. */
    fetchedAt: string | null;
    hours: { at: string; tempC: number; condition: WeatherCondition }[];
    days: WidgetDay[];
  } | null;
  /** The map, only once the rave's offline download holds it. */
  map?: WidgetMapInput | null;
  cardWords?: LiveCardWords | null;
  words?: WidgetWords | null;
  /**
   * Who sent it: the rave's own page (`rave`, the default) knows the map's
   * download, the stage you were last near and "follow me"; My raves (`list`)
   * sends every attended rave, so one never opened on this phone has widgets
   * too — and never overwrites what only the rave's page knows.
   */
  source?: 'rave' | 'list';
}

/* ------------------------------------------------------- Active rave --- */

export interface WidgetRave { editionId: string; gatesAt: string | null; endsAt: string; lat?: number | null; lng?: number | null }

/**
 * "Active rave" for a widget, among the ATTENDED raves the phone holds:
 * festival mode's own rule first — on now (a day before the gates to six
 * hours after the end), the nearest when two are and a fix says where you
 * are, else the one last opened in the app (`opened` — 2026-10-30, the user:
 * "the widget stuck on another rave while the app is a different rave"), else
 * the first to open — else the next one to open, else the one that ended last
 * (its thank-you card, until another becomes active). Null: none.
 */
export function widgetActiveRave(
  raves: readonly WidgetRave[], now: Date, here: { lat: number; lng: number } | null = null, opened: string | null = null,
): string | null {
  const t = now.getTime();
  const date = (iso: string | null) => (iso ? new Date(iso) : null);
  const on = raves.filter((r) => isOnNow({ gatesOpenAt: date(r.gatesAt), endsAt: date(r.endsAt) }, now));
  if (on.length) {
    const byDistance = here
      ? on.filter((r) => typeof r.lat === 'number' && typeof r.lng === 'number')
        .sort((a, b) => haversineMetres(here.lat, here.lng, a.lat!, a.lng!) - haversineMetres(here.lat, here.lng, b.lat!, b.lng!))
      : [];
    return (byDistance[0] ?? on.find((r) => r.editionId === opened) ?? [...on].sort(byGates)[0]!).editionId;
  }
  const coming = raves.filter((r) => Date.parse(r.endsAt) > t).sort(byGates);
  if (coming.length) return coming[0]!.editionId;
  const ended = [...raves].sort((a, b) => Date.parse(b.endsAt) - Date.parse(a.endsAt));
  return ended[0]?.editionId ?? null;
}

function byGates(a: WidgetRave, b: WidgetRave): number {
  const g = (r: WidgetRave) => (r.gatesAt ? Date.parse(r.gatesAt) : Date.parse(r.endsAt));
  return g(a) - g(b) || a.editionId.localeCompare(b.editionId);
}

/* ------------------------------------------------------- Live mode --- */

/**
 * The stage Live mode's widget shows when the app knows none of yours (no stage you were last near): the first
 * stage, in the rave's own order, with a set on now — else the one whose next set starts soonest. Null: nothing
 * left on any stage.
 */
export function widgetLiveStage(wp: Pick<WidgetPlan, 'stages' | 'acts'>, now: Date): string | null {
  const t = now.getTime();
  const known = new Set(wp.stages.map((s) => s.id));
  const playing = new Set(wp.acts.filter((a) => Date.parse(a.startAt) <= t && t < Date.parse(a.endAt)).map((a) => a.stageId));
  const on = wp.stages.find((s) => playing.has(s.id));
  if (on) return on.id;
  const next = wp.acts
    .filter((a) => known.has(a.stageId) && Date.parse(a.startAt) > t)
    .sort((a, b) => Date.parse(a.startAt) - Date.parse(b.startAt) || a.id.localeCompare(b.id))[0];
  return next?.stageId ?? null;
}

/**
 * Live mode's widget is the always-on card's own rule, on ONE stage: what plays there now and what is next there
 * (the 0.14.82 walk, Android: "it shows my next saved" — the widget is Live mode, not My lineup; your saved sets
 * are the My schedule widget's). So the card's plan carries NO saved sets, and the stage is yours — the one you
 * were last near — else `widgetLiveStage`.
 */
export function widgetCardPlan(wp: WidgetPlan, now: Date): LiveCardPlan {
  const t = now.getTime();
  const acts = wp.acts
    .filter((a) => Date.parse(a.endAt) > t && Date.parse(a.startAt) < t + WIDGET_CARD_WINDOW_MS)
    .sort((a, b) => Date.parse(a.startAt) - Date.parse(b.startAt) || a.id.localeCompare(b.id))
    .slice(0, 400);
  const gates = wp.gatesAt && t < Date.parse(wp.gatesAt) ? { at: wp.gatesAt, name: wp.festivalName || wp.rave } : null;
  const known = wp.stageId && wp.stages.some((s) => s.id === wp.stageId) ? wp.stageId : null;
  return {
    editionId: wp.editionId, rave: wp.rave, timezone: wp.timezone, endsAt: wp.endsAt,
    mode: wp.mode, stageId: known ?? widgetLiveStage(wp, now), radiusM: wp.radiusM,
    stages: wp.stages.map(({ x: _x, y: _y, trackId: _t, ...s }) => s),
    acts, saved: [],
    ...(wp.url ? { url: wp.url } : {}),
    ...(wp.logoUrl ? { logoUrl: wp.logoUrl } : {}),
    ...(wp.coverUrl ? { coverUrl: wp.coverUrl } : {}),
    ...(typeof wp.lat === 'number' && typeof wp.lng === 'number' ? { lat: wp.lat, lng: wp.lng } : {}),
    ...(wp.weather ? { weather: { units: wp.weather.units, hours: wp.weather.hours.filter((h) => Date.parse(h.at) + 3_600_000 > t).slice(0, 30) } } : {}),
    ...(gates ? { gates } : {}),
    ...(wp.cardWords ? { words: wp.cardWords } : {}),
  };
}

/** The set after the one playing, on the same stage — Live mode's widget's "Up next here" while a set plays. */
export function widgetStageNext(plan: Pick<LiveCardPlan, 'acts'>, stageId: string | null, playing: { startAt: string; endAt: string } | null): LiveCardAct | null {
  if (!stageId || !playing) return null;
  const from = Date.parse(playing.startAt);
  return plan.acts
    .filter((a) => a.stageId === stageId && Date.parse(a.startAt) > from)
    .sort((a, b) => Date.parse(a.startAt) - Date.parse(b.startAt) || a.id.localeCompare(b.id))[0] ?? null;
}

/* ------------------------------------------------------ The two lists --- */

/** A set as the lists carry it: times in epoch ms, the clock and weekday in the rave's zone. */
export interface WidgetSet { id: string; artist: string; stageId: string; startAt: number; endAt: number; clock: string; day: string; saved: boolean }
/** A sunrise or sunset row ("☀ Sunrise 05:31"). */
export interface WidgetSun { kind: 'sunrise' | 'sunset'; at: number; clock: string; day: string }
export type WidgetRow = { kind: 'set'; set: WidgetSet } | { kind: 'sun'; sun: WidgetSun };

/** Which sets a list shows: every chosen stage (`null`: all), or one alone; `mine`: the saved ones. */
export interface WidgetFilter { list: 'full' | 'mine'; stageIds: readonly string[] | null }

/**
 * The rows of a list at `at` (epoch ms) — the ONE rule Swift and Kotlin copy
 * (and `widgetFixture` checks): the sets that END after `at`, of the chosen
 * stages (Full schedule) or saved (My schedule), by start (then id); the
 * sunrises and sunsets between the FIRST set shown (or `at`, if later) and the
 * last one shown, each in its place in time — before the gates the days with
 * no set of the list never fill it with sun rows; at most `n` rows. A sun row never comes first when sets
 * follow it in the same minute — sets before sun at an equal instant.
 */
export function widgetRows(sets: readonly WidgetSet[], sun: readonly WidgetSun[], filter: WidgetFilter, at: number, n: number): WidgetRow[] {
  const chosen = filter.stageIds ? new Set(filter.stageIds) : null;
  const picked = sets
    .filter((s) => s.endAt > at && (filter.list === 'mine' ? s.saved : !chosen || chosen.has(s.stageId)))
    .sort((a, b) => a.startAt - b.startAt || a.id.localeCompare(b.id));
  const rows: { t: number; o: number; row: WidgetRow }[] = picked.map((s) => ({ t: Math.max(s.startAt, at), o: 0, row: { kind: 'set', set: s } }));
  const last = picked.length ? picked[Math.min(picked.length, n) - 1]!.startAt : -Infinity;
  const first = picked.length ? Math.max(at, picked[0]!.startAt) : Infinity;
  for (const s of sun) if (s.at > first && s.at <= last) rows.push({ t: s.at, o: 1, row: { kind: 'sun', sun: s } });
  return rows.sort((a, b) => a.t - b.t || a.o - b.o).slice(0, n).map((r) => r.row);
}

/** Every sunrise and sunset from the gates (or `from`) to the rave's end, at its place. */
export function widgetSunRows(lat: number, lng: number, timeZone: string, from: number, to: number, dayName: (iso: string) => string): WidgetSun[] {
  const out: WidgetSun[] = [];
  const DAY = 86_400_000;
  for (let t = from - DAY; t <= to + DAY; t += DAY) {
    const day = toOffsetISO(new Date(t), timeZone).slice(0, 10);
    const { sunrise, sunset } = sunTimes(day, lat, lng, timeZone);
    for (const [kind, d] of [['sunrise', sunrise], ['sunset', sunset]] as const) {
      if (!d || d.getTime() < from || d.getTime() > to || out.some((x) => x.at === d.getTime())) continue;
      out.push({ kind, at: d.getTime(), clock: wallClock(d, timeZone), day: dayName(d.toISOString()) });
    }
  }
  return out.sort((a, b) => a.at - b.at);
}

/**
 * When a widget's content changes, from `from` until `until`: every set's start
 * and end, every sun row, the gates and the rave's end — the timeline entries
 * the widgets are given (the app owns the schedule; a widget never decides it).
 */
export function widgetChanges(sets: readonly WidgetSet[], sun: readonly WidgetSun[], extra: readonly number[], from: number, until: number, max = 200): number[] {
  const all = new Set<number>();
  for (const s of sets) { all.add(s.startAt); all.add(s.endAt); }
  for (const s of sun) all.add(s.at);
  for (const x of extra) all.add(x);
  return [...all].filter((x) => x > from && x <= until).sort((a, b) => a - b).slice(0, max);
}

/* --------------------------------------------------------- Live map --- */

export interface WidgetFix { lat: number; lng: number; accuracy: number | null; at: number }

/** A mark on the cropped map: 0..1 fractions of the CROP. */
export interface WidgetMapMark { x: number; y: number }

export interface WidgetMapView {
  /** The part of the map image to draw, as 0..1 fractions of the image. */
  crop: { x: number; y: number; w: number; h: number };
  stages: (WidgetMapMark & { id: string })[];
  points: (WidgetMapMark & { id: string; name: string; category: string | null })[];
  /** Your GPS mark — only for a fresh fix on the map; `r` the accuracy as a fraction of the crop's width. */
  me: (WidgetMapMark & { r: number | null; at: number }) | null;
  /**
   * A fresh fix that lands OFF the map — you are outside the rave's map area (the user, 2026-10-29:
   * the Live map was "not showing you are outside of the map zone"). Its time, so a widget stops
   * saying it once the fix is older than `WIDGET_MARK_FRESH_MS`, as the mark does; null otherwise
   * (no fix, a stale one, a map with no survey points — then "No location").
   */
  outsideAt: number | null;
}

/** What the Live map's caption says about you: your mark, outside the map, or no location. */
export type WidgetWhere = 'here' | 'outside' | 'none';

/**
 * The caption's state at a widget entry `at` — the ONE rule both phones copy (`widgetWhereFixture`):
 * a mark whose fix is still fresh → `here`; else a fresh fix off the map → `outside`; else `none`.
 * Location off forgets the fix in the shell (`forgetFix`), so it is `none` — never `outside`.
 */
export function widgetWhere(view: Pick<WidgetMapView, 'me' | 'outsideAt'>, at: number): WidgetWhere {
  if (view.me && markIsFresh(view.me.at, at)) return 'here';
  if (view.outsideAt != null && markIsFresh(view.outsideAt, at)) return 'outside';
  return 'none';
}

/** How much of the map's width "zoomed around you" shows. */
export const WIDGET_MAP_ZOOM = 0.4;
/** Room around the site's stages, each side, as a share of the site (no fresh fix). */
const SITE_MARGIN = 0.2;

/** Is a fix fresh enough to draw (`WIDGET_MARK_FRESH_MS`)? */
export function markIsFresh(fixAt: number, at: number): boolean {
  return at - fixAt <= WIDGET_MARK_FRESH_MS && fixAt <= at + 60_000;
}

/**
 * The Live map at `at`: zoomed around you (a fresh fix that lands on the map),
 * else the whole site (every pinned stage in view; no pins: the whole map) — fitted to the widget's `aspect` (width ÷ height) and
 * kept inside the image; every stage and point of interest inside the crop as
 * a fraction of it; your mark with its accuracy. The warp is the map page's
 * own (`fitTps` / `geoToImage`).
 */
export function widgetMap(map: WidgetMapInput, stages: readonly WidgetStage[], fix: WidgetFix | null, at: number, aspect: number): WidgetMapView {
  const imageAspect = map.width / map.height;
  let me: { x: number; y: number; r: number | null; at: number } | null = null;
  let outsideAt: number | null = null;
  if (fix && markIsFresh(fix.at, at) && map.controlPoints.length >= 3) {
    try {
      const fit = fitTps(map.controlPoints);
      const p = geoToImage(fit, fix.lat, fix.lng);
      if (isOnMap(p, 0)) me = { x: p.x, y: p.y, r: fix.accuracy != null ? accuracyFraction(map, fix) : null, at: fix.at };
      else if (Number.isFinite(p.x) && Number.isFinite(p.y)) outsideAt = fix.at;
    } catch { me = null; }
  }
  // The crop in IMAGE fractions: its width w, height h such that (w·W)/(h·H) = aspect.
  // With no fresh fix: the whole SITE — every stage pinned on the drawing in view (a map
  // drawing is often much taller or wider than the site on it); no pins: the whole image.
  const pins = stages.filter((s) => typeof s.x === 'number' && typeof s.y === 'number');
  let w: number; let h: number; let cx = 0.5; let cy = 0.5;
  if (me) { w = WIDGET_MAP_ZOOM; h = (w * imageAspect) / aspect; cx = me.x; cy = me.y; }
  else if (pins.length) {
    const xs = pins.map((s) => s.x!); const ys = pins.map((s) => s.y!);
    const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
    cx = (x0 + x1) / 2; cy = (y0 + y1) / 2;
    w = Math.max((x1 - x0) * (1 + 2 * SITE_MARGIN), ((y1 - y0) * (1 + 2 * SITE_MARGIN) * aspect) / imageAspect, WIDGET_MAP_ZOOM / 2);
    h = (w * imageAspect) / aspect;
  } else if (imageAspect > aspect) { h = 1; w = aspect / imageAspect; }
  else { w = 1; h = imageAspect / aspect; }
  if (h > 1) { w /= h; h = 1; }
  if (w > 1) { h /= w; w = 1; }
  const x = clamp(cx - w / 2, 0, 1 - w); const y = clamp(cy - h / 2, 0, 1 - h);
  const crop = { x: round(x), y: round(y), w: round(w), h: round(h) };
  const inside = (px: number, py: number) => px >= crop.x && px <= crop.x + crop.w && py >= crop.y && py <= crop.y + crop.h;
  const rel = (px: number, py: number) => ({ x: round((px - crop.x) / crop.w), y: round((py - crop.y) / crop.h) });
  return {
    crop,
    stages: stages.filter((s) => typeof s.x === 'number' && typeof s.y === 'number' && inside(s.x, s.y)).map((s) => ({ id: s.id, ...rel(s.x!, s.y!) })),
    points: map.points.filter((p) => inside(p.x, p.y)).slice(0, MAX_WIDGET_POIS).map((p) => ({ id: p.id, name: p.name, category: p.category, ...rel(p.x, p.y) })),
    me: me ? { ...rel(me.x, me.y), r: me.r != null ? round(me.r / crop.w) : null, at: me.at } : null,
    outsideAt,
  };
}

/** The fix's accuracy (metres) as a fraction of the image's width, from the map's own scale. */
function accuracyFraction(map: WidgetMapInput, fix: WidgetFix): number | null {
  const [a, b] = farthestPair(map.controlPoints);
  if (!a || !b || fix.accuracy == null) return null;
  const metres = haversineMetres(a.lat, a.lng, b.lat, b.lng);
  const frac = Math.hypot((a.x - b.x), (a.y - b.y) * (map.height / map.width));
  return metres > 0 ? (fix.accuracy / metres) * frac : null;
}

function farthestPair(cs: readonly ControlPoint[]): [ControlPoint | undefined, ControlPoint | undefined] {
  let best: [ControlPoint | undefined, ControlPoint | undefined] = [undefined, undefined]; let d = -1;
  for (let i = 0; i < cs.length; i++) for (let j = i + 1; j < cs.length; j++) {
    const e = Math.hypot(cs[i]!.x - cs[j]!.x, cs[i]!.y - cs[j]!.y);
    if (e > d) { d = e; best = [cs[i], cs[j]]; }
  }
  return best;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const round = (v: number) => Math.round(v * 10_000) / 10_000;

/* ---------------------------------------------------------- Fixture --- */

/** `widgetWhere` as data — Swift's `widgetWhere` and Kotlin's `WidgetRules.where` follow the same cases. */
export const widgetWhereFixture = [
  { name: 'a fresh mark', me: 1_000, outsideAt: null, at: 1_000 + 60_000, where: 'here' },
  { name: 'a mark 16 minutes old', me: 1_000, outsideAt: null, at: 1_000 + 16 * 60_000, where: 'none' },
  { name: 'a fresh fix off the map', me: null, outsideAt: 1_000, at: 1_000 + 14 * 60_000, where: 'outside' },
  { name: 'off the map, 16 minutes ago', me: null, outsideAt: 1_000, at: 1_000 + 16 * 60_000, where: 'none' },
  { name: 'no fix', me: null, outsideAt: null, at: 1_000, where: 'none' },
] satisfies { name: string; me: number | null; outsideAt: number | null; at: number; where: WidgetWhere }[];

/**
 * The lists' rule as data — Swift and Kotlin read the same cases in their own
 * tests when built; `widgets.test.ts` checks `widgetRows` against them here.
 */
export const widgetFixture = {
  sets: [
    { id: 'a', artist: 'Shpongle', stageId: 'dome', startAt: 1_000, endAt: 5_000, clock: '22:00', day: 'Fri', saved: true },
    { id: 'b', artist: 'Ott', stageId: 'pumpui', startAt: 2_000, endAt: 6_000, clock: '23:30', day: 'Fri', saved: false },
    { id: 'c', artist: 'Tipper', stageId: 'dome', startAt: 5_000, endAt: 9_000, clock: '01:00', day: 'Sat', saved: true },
    { id: 'd', artist: 'Kalya', stageId: 'pumpui', startAt: 6_000, endAt: 9_000, clock: '02:30', day: 'Sat', saved: false },
  ] satisfies WidgetSet[],
  sun: [{ kind: 'sunset', at: 500, clock: '20:28', day: 'Thu' }, { kind: 'sunrise', at: 5_500, clock: '05:31', day: 'Sat' }] satisfies WidgetSun[],
  cases: [
    { name: 'full, all stages, at 0', filter: { list: 'full', stageIds: null }, at: 0, n: 6, rows: ['a', 'b', 'c', 'sunrise', 'd'] },
    { name: 'full, the dome only, at 5000', filter: { list: 'full', stageIds: ['dome'] }, at: 5_000, n: 6, rows: ['c'] },
    { name: 'mine, at 3000', filter: { list: 'mine', stageIds: null }, at: 3_000, n: 6, rows: ['a', 'c'] },
    { name: 'full, at 3000, three rows', filter: { list: 'full', stageIds: null }, at: 3_000, n: 3, rows: ['a', 'b', 'c'] },
    { name: 'full, at 9000: nothing left', filter: { list: 'full', stageIds: null }, at: 9_000, n: 6, rows: [] },
    { name: 'mine, at 0 (before the first set): no sun row before the first set', filter: { list: 'mine', stageIds: null }, at: 0, n: 6, rows: ['a', 'c'] },
  ] satisfies { name: string; filter: WidgetFilter; at: number; n: number; rows: string[] }[],
};
